function normalizeOptionalString(value) {
  return String(value || '').trim()
}

function normalizeSearchText(value) {
  return normalizeOptionalString(value)
    .toLowerCase()
    .replace(/[_/\\-]+/g, ' ')
    .replace(/[^a-z0-9. ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function uniqueOrdered(values) {
  const result = []
  const seen = new Set()

  for (const value of values || []) {
    const normalized = normalizeOptionalString(value)
    const dedupeKey = normalized.toLowerCase()
    if (!normalized || seen.has(dedupeKey)) {
      continue
    }

    seen.add(dedupeKey)
    result.push(normalized)
  }

  return result
}

function formatDisplayText(value) {
  return normalizeOptionalString(value)
    .replace(/[_/\\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ')
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeOptionalString(entry)).filter(Boolean)
  }

  const normalized = normalizeOptionalString(value)
  if (!normalized) {
    return []
  }

  return normalized.split('|').map((entry) => normalizeOptionalString(entry)).filter(Boolean)
}

export function buildScopePickerPrimaryLabel(scopeOption) {
  const commonNames = normalizeStringArray(scopeOption?.common_names)
  const primaryCommonName = commonNames[0]

  return primaryCommonName
    ? formatDisplayText(primaryCommonName)
    : normalizeOptionalString(scopeOption?.scope_name)
    || normalizeOptionalString(scopeOption?.scope_id)
    || '--'
}

export function buildScopePickerCommonNames(scopeOption) {
  const tradeTags = Array.isArray(scopeOption?.trade_tags)
    ? scopeOption.trade_tags
    : []
  const commonNames = normalizeStringArray(scopeOption?.common_names)

  return uniqueOrdered([
    ...commonNames,
    ...tradeTags,
    scopeOption?.scope_family
  ])
}

export function formatScopePickerOptionLabel(scopeOption) {
  if (!scopeOption) {
    return '--'
  }

  const primaryLabel = buildScopePickerPrimaryLabel(scopeOption)
  const canonicalName = normalizeOptionalString(scopeOption.scope_name)
  const aliasSummary = buildScopePickerCommonNames(scopeOption)
    .filter((value) => {
      const normalized = normalizeSearchText(value)
      return normalized
        && normalized !== normalizeSearchText(primaryLabel)
        && normalized !== normalizeSearchText(canonicalName)
    })
    .slice(0, 4)
    .map(formatDisplayText)

  const parts = [
    primaryLabel,
    canonicalName && normalizeSearchText(canonicalName) !== normalizeSearchText(primaryLabel)
      ? `canonical: ${canonicalName}`
      : null,
    aliasSummary.length ? `also: ${aliasSummary.join(', ')}` : null,
    scopeOption.scope_id ? `id: ${scopeOption.scope_id}` : null,
    scopeOption.scope_family ? `family: ${scopeOption.scope_family}` : null,
    scopeOption.csi_code ? `CSI ${scopeOption.csi_code}` : null
  ].filter(Boolean)

  return parts.join(' | ')
}

export function buildScopePickerSearchText(scopeOption) {
  if (!scopeOption) {
    return ''
  }

  return normalizeSearchText([
    buildScopePickerPrimaryLabel(scopeOption),
    scopeOption.scope_name,
    scopeOption.scope_id,
    scopeOption.scope_family,
    scopeOption.csi_code,
    ...buildScopePickerCommonNames(scopeOption)
  ].filter(Boolean).join(' '))
}

export function sortScopePickerOptions(scopeOptions) {
  return [...(Array.isArray(scopeOptions) ? scopeOptions : [])].sort((left, right) => {
    const leftPrimary = buildScopePickerPrimaryLabel(left)
    const rightPrimary = buildScopePickerPrimaryLabel(right)
    const primaryCompare = leftPrimary.localeCompare(rightPrimary, undefined, {
      sensitivity: 'base',
      numeric: true
    })
    if (primaryCompare !== 0) {
      return primaryCompare
    }

    const leftCanonical = normalizeOptionalString(left?.scope_name || left?.scope_id)
    const rightCanonical = normalizeOptionalString(right?.scope_name || right?.scope_id)
    const canonicalCompare = leftCanonical.localeCompare(rightCanonical, undefined, {
      sensitivity: 'base',
      numeric: true
    })
    if (canonicalCompare !== 0) {
      return canonicalCompare
    }

    return normalizeOptionalString(left?.scope_id).localeCompare(normalizeOptionalString(right?.scope_id), undefined, {
      sensitivity: 'base',
      numeric: true
    })
  })
}

export function filterScopePickerOptions(scopeOptions, searchText) {
  const sortedOptions = sortScopePickerOptions(scopeOptions)
  const normalizedSearch = normalizeSearchText(searchText)

  if (!normalizedSearch) {
    return sortedOptions
  }

  return sortedOptions.filter((scopeOption) =>
    buildScopePickerSearchText(scopeOption).includes(normalizedSearch)
  )
}
